import {
  Drawer,
  Button,
  Row,
  Col,
  Space,
  Radio,
  Alert,
  Table,
  Spin,
  Tag,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../shared/types/common";
import { showNotification } from "../../../shared/utils/utils";
import {
  getCandidateList,
  getJobDescriptionSkills,
  runScreening,
} from "../../../core/store/reducers/candidate-screening.slice";

interface Props {
  onClose: () => void;
  visible: boolean;
  jobDescription: any; // the JD to screen against
  onSubmit?: () => void;
}

const RunScreeningComponent = ({
  onClose,
  visible,
  jobDescription,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const { candidateList, loadingCandidates, screening } = useSelector(
    (state: RootState) => state.candidateScreening
  );
  const [mode, setMode] = useState<"all" | "selected">("all");
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // Extracted skills for the selected JD (for the pre-run sanity check).
  const [skills, setSkills] = useState<any>(null);
  const [loadingSkills, setLoadingSkills] = useState(false);

  useEffect(() => {
    if (visible) {
      dispatch(getCandidateList({ params: { page: 1, limit: 1000 } }));
      setMode("all");
      setSelectedRowKeys([]);
      setSkills(null);
      if (jobDescription?.id) {
        setLoadingSkills(true);
        dispatch(getJobDescriptionSkills(jobDescription.id)).then((val: any) => {
          setLoadingSkills(false);
          setSkills(val?.payload?.data?.data ?? null);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, jobDescription?.id, dispatch]);

  const dataSource = useMemo(
    () =>
      (candidateList || []).map((c: any, index: number) => ({
        key: c?.id ?? index,
        ...c,
      })),
    [candidateList]
  );

  const columns = [
    { title: "Name", dataIndex: "fullName", key: "fullName" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
  ];

  const handleRun = () => {
    if (mode === "selected" && selectedRowKeys.length === 0) {
      showNotification("Please select at least one candidate.", "warning");
      return;
    }
    const body: any = { jobDescriptionId: jobDescription?.id };
    if (mode === "selected") {
      body.candidateIds = selectedRowKeys;
    }
    dispatch(runScreening(body)).then((val: any) => {
      if (val?.payload?.data) {
        showNotification("Screening started successfully.", "success");
        onSubmit();
      }
    });
  };

  return (
    <Drawer
      maskClosable={false}
      title={<strong>Run Screening</strong>}
      className="form-drawer"
      width={720}
      onClose={onClose}
      open={visible}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Screening against: ${jobDescription?.title || "—"}`}
        description="Scores are deterministic (locked prompt, temperature 0, cached embeddings). Re-running produces the same ranking."
      />

      {loadingSkills ? (
        <div style={{ marginBottom: 16 }}>
          <Spin size="small" /> <span style={{ fontSize: 12 }}>Checking JD skills…</span>
        </div>
      ) : (
        skills && (
          <div style={{ marginBottom: 16 }}>
            {!skills.hasMustHave ? (
              <Alert
                type="warning"
                showIcon
                message="This job description has no must-have skills"
                description="Nothing concrete was extracted to screen against, so the ranking will not be meaningful. Add clear required skills to the JD text, then re-open this dialog."
              />
            ) : (
              <div>
                <div style={{ fontSize: 13, marginBottom: 4 }}>
                  Must-have skills:
                </div>
                <div style={{ marginBottom: 8 }}>
                  {skills.mustHave.map((s: string, i: number) => (
                    <Tag color="blue" key={`must-${i}`}>
                      {s}
                    </Tag>
                  ))}
                </div>
                {skills.niceToHave?.length > 0 && (
                  <>
                    <div style={{ fontSize: 13, marginBottom: 4 }}>
                      Nice-to-have:
                    </div>
                    <div>
                      {skills.niceToHave.map((s: string, i: number) => (
                        <Tag key={`nice-${i}`}>{s}</Tag>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )
      )}

      <Radio.Group
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        style={{ marginBottom: 16 }}
      >
        <Radio value="all">Screen all candidates</Radio>
        <Radio value="selected">Screen selected candidates</Radio>
      </Radio.Group>

      {mode === "selected" &&
        (loadingCandidates ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <Table
            rowKey="key"
            size="small"
            columns={columns}
            dataSource={dataSource}
            pagination={{ pageSize: 8 }}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys),
            }}
          />
        ))}

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              loading={screening}
              onClick={handleRun}
              disabled={!jobDescription?.id}
            >
              Run Screening
            </Button>
          </Space>
        </Col>
      </Row>
    </Drawer>
  );
};

export default RunScreeningComponent;
